import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { SuiContainer } from '@splashthat/sui';
import ColorPickerEditor from 'common/components/ColorPicker';
import BorderEditor from 'common/components/BorderPicker';

import {
    setFormFieldsBorderWeight,
    setFormFieldsBorderRadius,
    setFormFieldsBorderSizeTop,
    setFormFieldsBorderSizeBottom,
    setFormFieldsBorderSizeRight,
    setFormFieldsBorderSizeLeft,
    setFormFieldsBorderColorDefault,
    setFormFieldsBorderColorHover,
    setFormFieldsBorderColorFocus,
} from 'common/entities/form/styleConfiguration/actions';
import {
    DEFAULT_INPUT_BORDER_COLOR,
    DEFAULT_INPUT_BORDER_COLOR_HOVER,
    DEFAULT_INPUT_BORDER_COLOR_FOCUS,
    DEFAULT_INPUT_BORDER_RADIUS,
} from 'common/entities/form/styleConfiguration/defaultValues';
import { getDefaultStyle, styleShapePropTypes } from 'common/entities/form/styleConfiguration/utils';
import { getFormBorderStyles } from 'common/entities/form/styleConfiguration/selectors';
import { getColors } from 'common/entities/color/selectors';
import { getFormId } from '../../../../selectors';

import { COLOR_PICKER_DEFAULT, COLOR_PICKER_HOVER, COLOR_PICKER_FOCUS } from '../../../../constants/formStyleLabels';

import { FORM_FIELDS_QA } from '../../../../utils/qaIdConstants';

const InputBorderPanel = ({
    formId,
    defaultColor,
    hoverColor,
    focusColor,
    borderWidths,
    borderRadius,
    colorList,
    componentLabel,
    ...props
}) => (
    <SuiContainer className={'BorderColorPicker'}>
        <BorderEditor
            borderWidths={borderWidths}
            onBorderWidthsChanged={(width) => props.setFormFieldsBorderWeight(formId, width)}
            borderRadius={borderRadius}
            onBorderRadiusChanged={(radius) => props.setFormFieldsBorderRadius(formId, radius)}
            onChangeTopBorderWidth={(width) => props.setFormFieldsBorderSizeTop(formId, width)}
            onChangeBottomBorderWidth={(width) => props.setFormFieldsBorderSizeBottom(formId, width)}
            onChangeRightBorderWidth={(width) => props.setFormFieldsBorderSizeRight(formId, width)}
            onChangeLeftBorderWidth={(width) => props.setFormFieldsBorderSizeLeft(formId, width)}
            borderOptionsQa={`${FORM_FIELDS_QA}--borderOptions`}
            borderDropdownQa={`${FORM_FIELDS_QA}--borderDropdown`}
            componentLabel={componentLabel}
        />
        <ColorPickerEditor
            colorList={colorList}
            firstColor={defaultColor}
            firstColorLabel={COLOR_PICKER_DEFAULT}
            changeFirstColor={(id) => props.setFormFieldsBorderColorDefault(formId, id)}
            qaId={FORM_FIELDS_QA}
        />
        <ColorPickerEditor
            colorList={colorList}
            firstColor={hoverColor}
            firstColorLabel={COLOR_PICKER_HOVER}
            changeFirstColor={(id) => props.setFormFieldsBorderColorHover(formId, id)}
            secondColor={focusColor}
            secondColorLabel={COLOR_PICKER_FOCUS}
            changeSecondColor={(id) => props.setFormFieldsBorderColorFocus(formId, id)}
            qaId={`${FORM_FIELDS_QA}----hoverFocus`}
        />
    </SuiContainer>
);

InputBorderPanel.propTypes = {
    defaultColor: styleShapePropTypes,
    hoverColor: styleShapePropTypes,
    focusColor: styleShapePropTypes,
    borderRadius: PropTypes.number,
    borderWidths: PropTypes.shape({
        top: PropTypes.number,
        right: PropTypes.number,
        bottom: PropTypes.number,
        left: PropTypes.number,
    }),
    formId: PropTypes.string.isRequired,
    colorList: PropTypes.arrayOf(PropTypes.object).isRequired,
    setFormFieldsBorderWeight: PropTypes.func.isRequired,
    setFormFieldsBorderRadius: PropTypes.func.isRequired,
    setFormFieldsBorderSizeTop: PropTypes.func.isRequired,
    setFormFieldsBorderSizeBottom: PropTypes.func.isRequired,
    setFormFieldsBorderSizeRight: PropTypes.func.isRequired,
    setFormFieldsBorderSizeLeft: PropTypes.func.isRequired,
    setFormFieldsBorderColorDefault: PropTypes.func.isRequired,
    setFormFieldsBorderColorHover: PropTypes.func.isRequired,
    setFormFieldsBorderColorFocus: PropTypes.func.isRequired,
    componentLabel: PropTypes.string,
};

InputBorderPanel.defaultProps = {
    defaultColor: getDefaultStyle(DEFAULT_INPUT_BORDER_COLOR),
    hoverColor: getDefaultStyle(DEFAULT_INPUT_BORDER_COLOR_HOVER),
    focusColor: getDefaultStyle(DEFAULT_INPUT_BORDER_COLOR_FOCUS),
    borderRadius: DEFAULT_INPUT_BORDER_RADIUS,
    borderWidths: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    },
    componentLabel: '',
};

const mapStateToProps = (state) => {
    const formId = getFormId(state);
    const colorList = getColors(state);
    const { colors, radius, ...borderWidths } = getFormBorderStyles(state, formId);

    const { default: defaultColor, hover: hoverColor, focus: focusColor } = colors;

    return {
        formId,
        defaultColor,
        hoverColor,
        focusColor,
        borderRadius: radius,
        borderWidths,
        colorList,
    };
};

const mapDispatchToProps = {
    setFormFieldsBorderWeight,
    setFormFieldsBorderRadius,
    setFormFieldsBorderSizeTop,
    setFormFieldsBorderSizeBottom,
    setFormFieldsBorderSizeRight,
    setFormFieldsBorderSizeLeft,
    setFormFieldsBorderColorDefault,
    setFormFieldsBorderColorHover,
    setFormFieldsBorderColorFocus,
};

export default connect(mapStateToProps, mapDispatchToProps)(InputBorderPanel);
